import { StyleSheet } from 'react-native';

import type { useTheme } from './theme';

// Styles for <HistoryPanel> and its row components, split out when the LG-49
// rewrite pushed the panel past the 500-line cap. Same sibling idiom as
// `FocusPanel.styles.ts` / `FocusPanelEffort.styles.ts`: the component keeps
// the logic, the sheet keeps the tokens.

type Theme = ReturnType<typeof useTheme>;

export type HistoryPanelStyles = ReturnType<typeof makeStyles>;

export const makeStyles = (theme: Theme) => {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      gap: s.s2,
      minHeight: 44,
    },
    headerLabel: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    headerCount: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      flex: 1,
    },
    // The blink switch in the header. Quiet by intent, but ink2/sm not
    // ink3/xs: this is the only visible way to reach the preference from the
    // panel, so it has to be readable to work at all — the same reasoning
    // `EffortNudgeOptOut` records for its own off-switch.
    blinkToggle: {
      minHeight: 44,
      justifyContent: 'center',
      paddingHorizontal: s.s2,
    },
    blinkTogglePressed: { opacity: 0.6 },
    blinkToggleText: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      fontWeight: '600',
    },
    // One performed set: date · numbers · effort, on a single readable line.
    setLine: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: s.s2,
      paddingVertical: 2,
    },
    rowMain: {
      flex: 1,
    },
    rowDateLine: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      marginBottom: 2,
    },
    sourceName: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      marginBottom: 2,
    },
    emptyMsg: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
      paddingVertical: s.s1,
    },
    rowDate: {
      color: p.ink2,
      fontSize: ty.sizes.xs,
      // Dates line up down the column so five rows read as a list, not as
      // five sentences of different lengths.
      minWidth: 54,
    },
    // EQ-1 — the machine marker. Glyph AND word, never the glyph alone.
    machineTag: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s1,
    },
    machineTagText: {
      // `ink2`, not `ink3`: on the two light families ink3 lands at ~4.80 on
      // surface2 — over the floor by a hair, and this reads at xs.
      color: p.ink2,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
    },
    setChip: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontVariant: ['tabular-nums'],
      paddingHorizontal: s.s2,
      paddingVertical: 2,
      backgroundColor: p.surface,
      borderRadius: r.r1,
    },
    // The effort rides quieter than the load: it qualifies the number, it is
    // not the number. Still `ink2`, which clears AA on surface2 everywhere.
    effort: {
      color: p.ink2,
      fontSize: ty.sizes.xs,
      fontWeight: '600',
      fontVariant: ['tabular-nums'],
    },
        // The all-time reference, set apart from the recent five by a rule so it
    // is not misread as a sixth recent set.
    bestBlock: {
      borderTopWidth: 1,
      borderTopColor: p.border,
      paddingTop: s.s2,
      marginTop: s.s1,
    },
    bestBadge: {
      color: p.ink3,
      fontSize: ty.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      marginBottom: 2,
    },
            // ── LG-59: the three-section widget ──────────────────────────────
    // The sections stack inside the sheet; the sheet owns the outer padding,
    // so this only sets the rhythm between them.
    sections: {
      gap: s.s3,
      marginTop: s.s2,
    },
    // THE row of all three sections: content on the left, «Copia» on the
    // right. The gap here is HORIZONTAL — it separates the text from the
    // button, and it does nothing at all about the rows above and below.
    // ⛔ The one that keeps tap areas apart is `rowStack` below; this comment
    // used to claim this gap did that job, which was simply false (a11y
    // audit, 2026-09-02).
    copyRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s3,
      paddingVertical: s.s1,
      minHeight: 44,
    },
    // ⛔ Vertical breathing room between stacked rows, and it is load-bearing,
    // not cosmetic: each row's copy button is 40 tall with a hitSlop of 4 top
    // and bottom (48 ≥ the floor), so touching rows would let one row's tap
    // area reach ~2pt into its neighbours and silently steal their taps.
    // s2 (4) > 2 on each side, so they cannot meet.
    rowStack: {
      gap: s.s2,
    },
    copyRowMain: {
      flex: 1,
      gap: 2,
    },
    // The value in use. ⛔ Not colour alone: it carries the «in uso» word as
    // well, because a tinted left edge is invisible to the athlete this app
    // is built for.
    copyRowInUse: {
      borderLeftWidth: 3,
      borderLeftColor: p.brandText,
      paddingLeft: s.s2,
      marginLeft: -s.s2,
    },
    candidateWeight: {
      color: p.ink,
      fontSize: ty.sizes.base,
      fontWeight: '700',
      fontVariant: ['tabular-nums'],
    },
    // ⛔ `sm`, not `xs`. This sentence is what tells the athlete WHICH weight
    // to take — Antenore, 2026-09-02: «se la linea non è chiara, l'utente non
    // sa cosa prendere» — and it was shipped at 11pt, the smallest size in the
    // scale, to a reader with reduced vision. Readability beats the hierarchy
    // here; the weight above it stays bigger and bolder, so the order still
    // reads (a11y audit, 2026-09-02).
    candidateLabel: {
      color: p.ink2,
      fontSize: ty.sizes.sm,
    },
    // Keeps the proposal caption off the section divider when it comes first.
    proposalCaption: { paddingVertical: s.s1 },
    // The word that says which one the cell is holding — never the tint on
    // its own (a11y floor).
    inUseBadge: {
      color: p.brandText,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
      letterSpacing: 0.4,
      textTransform: 'uppercase',
    },
    candidateHead: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s.s2,
      flexWrap: 'wrap',
    },
  });
};
